import { useState, useEffect, useCallback } from 'react';
import { ArrowUpRight, AlertCircle, Shield, ChevronDown, Wallet, RefreshCw, CheckCircle, Loader2, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { walletAPI } from '../../services/api';

import { fmtFixed } from '../../utils/format';
const WithdrawalTab = ({ balance, onSuccess }) => {
  const [selectedCrypto, setSelectedCrypto] = useState('USDT');
  const [selectedNetwork, setSelectedNetwork] = useState('TRC20');
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNetworkDropdown, setShowNetworkDropdown] = useState(false);
  const [cryptoHoldings, setCryptoHoldings] = useState([]);
  const [loadingHoldings, setLoadingHoldings] = useState(true);
  const [totalPortfolioValue, setTotalPortfolioValue] = useState(0);
  const [withdrawalSuccess, setWithdrawalSuccess] = useState(false);

  // Fetch crypto holdings on mount and when crypto changes
  const fetchCryptoHoldings = useCallback(async () => {
    setLoadingHoldings(true);
    try {
      const data = await walletAPI.getCryptoHoldings();
      setCryptoHoldings(data.holdings || []);
      setTotalPortfolioValue(data.total_usd_value || 0);
    } catch (error) {
      console.error('Error fetching crypto holdings:', error);
      toast.error('Failed to load crypto holdings');
    } finally {
      setLoadingHoldings(false);
    }
  }, []);

  useEffect(() => {
    fetchCryptoHoldings();
  }, [fetchCryptoHoldings]);

  // Get balance for selected crypto
  const getSelectedCryptoBalance = () => {
    const holding = cryptoHoldings.find(h => h.symbol === selectedCrypto);
    return holding?.balance || 0;
  };

  const getSelectedCryptoUsdValue = () => {
    const holding = cryptoHoldings.find(h => h.symbol === selectedCrypto);
    return holding?.usd_value || 0;
  };

  const getSelectedCryptoPrice = () => {
    const holding = cryptoHoldings.find(h => h.symbol === selectedCrypto);
    return holding?.price || 0;
  };

  // Available networks for each cryptocurrency with fees
  const cryptoNetworks = {
    BTC: [
      { id: 'Bitcoin', name: 'Bitcoin Network', minWithdrawal: 0.0001, fee: 0.00005 }
    ],
    ETH: [
      { id: 'ERC20', name: 'Ethereum (ERC20)', minWithdrawal: 0.001, fee: 0.0005 }
    ],
    USDT: [
      { id: 'TRC20', name: 'Tron (TRC20)', minWithdrawal: 1, fee: 1 },
      { id: 'ERC20', name: 'Ethereum (ERC20)', minWithdrawal: 10, fee: 5 },
      { id: 'BEP20', name: 'BNB Smart Chain (BEP20)', minWithdrawal: 1, fee: 0.8 }
    ],
    BNB: [
      { id: 'BEP20', name: 'BNB Smart Chain (BEP20)', minWithdrawal: 0.01, fee: 0.0005 },
      { id: 'BEP2', name: 'BNB Beacon Chain (BEP2)', minWithdrawal: 0.01, fee: 0.001 }
    ],
    SOL: [
      { id: 'Solana', name: 'Solana Network', minWithdrawal: 0.01, fee: 0.000005 }
    ],
    ADA: [
      { id: 'Cardano', name: 'Cardano Network', minWithdrawal: 1, fee: 0.17 }
    ],
    XRP: [
      { id: 'XRP', name: 'XRP Ledger', minWithdrawal: 1, fee: 0.25 }
    ],
    DOT: [
      { id: 'Polkadot', name: 'Polkadot Network', minWithdrawal: 0.1, fee: 0.01 }
    ],
  };

  const cryptoList = [
    { symbol: 'BTC', name: 'Bitcoin' },
    { symbol: 'ETH', name: 'Ethereum' },
    { symbol: 'USDT', name: 'Tether' },
    { symbol: 'BNB', name: 'BNB' },
    { symbol: 'SOL', name: 'Solana' },
    { symbol: 'ADA', name: 'Cardano' },
    { symbol: 'XRP', name: 'XRP' },
    { symbol: 'DOT', name: 'Polkadot' },
  ];

  const availableNetworks = cryptoNetworks[selectedCrypto] || [];
  const currentNetwork = availableNetworks.find(n => n.id === selectedNetwork) || availableNetworks[0];
  
  const withdrawalAmount = parseFloat(amount) || 0;
  const networkFee = currentNetwork?.fee || 0;
  const totalAmount = withdrawalAmount + networkFee;
  const receiveAmount = withdrawalAmount;
  const cryptoBalance = getSelectedCryptoBalance();
  const cryptoPrice = getSelectedCryptoPrice();
  const withdrawalUsdValue = withdrawalAmount * cryptoPrice;

  const handleCryptoChange = (symbol) => {
    setSelectedCrypto(symbol);
    setAmount('');
    // Set default network for the selected crypto
    const networks = cryptoNetworks[symbol] || [];
    if (networks.length > 0) {
      setSelectedNetwork(networks[0].id);
    }
  };

  const handleMaxAmount = () => {
    const maxWithdrawable = Math.max(0, cryptoBalance - networkFee);
    setAmount(maxWithdrawable.toString());
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    setWithdrawalSuccess(false);

    // Validation
    if (!amount || withdrawalAmount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (withdrawalAmount < currentNetwork.minWithdrawal) {
      toast.error(`Minimum withdrawal is ${currentNetwork.minWithdrawal} ${selectedCrypto}`);
      return;
    }

    if (!address || address.length < 10) {
      toast.error('Please enter a valid withdrawal address');
      return;
    }

    if (totalAmount > cryptoBalance) {
      toast.error(`Insufficient ${selectedCrypto} balance. Available: ${formatCryptoAmount(cryptoBalance, selectedCrypto)} ${selectedCrypto}`);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await api.post('/withdrawals/submit', {
        amount: withdrawalAmount,
        crypto_type: selectedCrypto,
        network: currentNetwork.name,
        wallet_address: address,
        notes: ''
      });

      if (response.data.success) {
        setWithdrawalSuccess(true);
        toast.success(
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <span>Withdrawal of {formatCryptoAmount(withdrawalAmount, selectedCrypto)} {selectedCrypto} submitted!</span>
          </div>
        );
        setAmount('');
        setAddress('');
        
        // Refresh holdings to show updated balance
        setTimeout(() => {
          fetchCryptoHoldings();
          if (onSuccess) onSuccess();
        }, 1000);
      }
    } catch (error) {
      console.error('Withdrawal error:', error);
      const { handleKycGateError } = await import('../../utils/kycGateToast.js');
      if (!handleKycGateError(error, (path) => (window.location.href = path))) {
        const errorMsg = error.response?.data?.detail || 'Failed to submit withdrawal request';
        toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to submit withdrawal request');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format crypto amount based on type
  const formatCryptoAmount = (amount, symbol) => {
    if (symbol === 'USDT' || symbol === 'USD') {
      return fmtFixed(amount, 2);
    }
    if (amount < 0.0001) {
      return fmtFixed(amount, 8);
    }
    if (amount < 1) {
      return fmtFixed(amount, 6);
    }
    return fmtFixed(amount, 4);
  };

  return (
    <div className="space-y-5 py-2">
      {/* Header with Portfolio Value */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold mb-1">Withdraw Cryptocurrency</h2>
          <p className="text-gray-400 text-sm">
            Withdraw funds to an external wallet
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400">Total Portfolio</p>
          <p className="text-lg font-bold text-purple-400">
            ${totalPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
      </div>

      {/* Selected Crypto Balance Card - Purple Accent */}
      <div className="bg-gradient-to-br from-purple-900/40 to-indigo-900/40 rounded-xl p-4 border border-purple-500/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-purple-500/20 rounded-lg">
              <Wallet className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-gray-400">Available {selectedCrypto}</p>
              <p className="text-xl font-bold text-white">
                {loadingHoldings ? (
                  <span className="animate-pulse">Loading...</span>
                ) : (
                  `${formatCryptoAmount(cryptoBalance, selectedCrypto)} ${selectedCrypto}`
                )}
              </p>
              <p className="text-xs text-purple-300">
                ≈ ${getSelectedCryptoUsdValue().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
              </p>
            </div>
          </div>
          <button
            onClick={fetchCryptoHoldings}
            disabled={loadingHoldings}
            className="p-2 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 text-purple-400 ${loadingHoldings ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <form onSubmit={handleWithdraw} className="space-y-5">
        {/* Cryptocurrency Selection - Purple Accent */}
        <div>
          <label className="text-sm font-semibold text-gray-300 mb-2 block">Select Cryptocurrency</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
            {cryptoList.map((crypto) => {
              const holding = cryptoHoldings.find(h => h.symbol === crypto.symbol);
              const cryptoBalance = holding?.balance || 0;
              const usdValue = holding?.usd_value || 0;
              const hasBalance = cryptoBalance > 0;
              
              return (
                <button
                  key={crypto.symbol}
                  type="button"
                  onClick={() => handleCryptoChange(crypto.symbol)}
                  className={`p-2 rounded-xl transition-all duration-200 border ${
                    selectedCrypto === crypto.symbol
                      ? 'bg-gradient-to-br from-purple-600 to-indigo-600 border-transparent text-white shadow-lg shadow-purple-500/30'
                      : 'bg-gray-900/50 border-purple-500/20 text-gray-400 hover:border-purple-500/50 hover:bg-purple-900/20'
                  }`}
                >
                  <div className={`text-xs font-bold ${selectedCrypto === crypto.symbol ? 'text-white' : 'text-purple-300'}`}>
                    {crypto.symbol}
                  </div>
                  <div className={`text-xs truncate ${selectedCrypto === crypto.symbol ? 'text-purple-100' : hasBalance ? 'text-green-400' : 'text-gray-500'}`}>
                    {cryptoBalance > 0 ? formatCryptoAmount(cryptoBalance, crypto.symbol) : '0'}
                  </div>
                  <div className={`text-xs truncate ${selectedCrypto === crypto.symbol ? 'text-purple-200' : 'text-gray-600'}`}>
                    ${fmtFixed(usdValue, 2)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Network Selection - Purple Accent */}
        {availableNetworks.length > 1 && (
          <div>
            <label className="text-sm font-semibold text-gray-300 mb-2 block">Select Network</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowNetworkDropdown(!showNetworkDropdown)}
                className="w-full px-4 py-3 bg-gray-900/50 text-white rounded-xl border border-purple-500/30 hover:border-purple-500/50 transition-all flex items-center justify-between"
              >
                <div className="text-left">
                  <div className="font-medium text-sm">{currentNetwork?.name}</div>
                  <div className="text-xs text-gray-400">Fee: {currentNetwork?.fee} {selectedCrypto}</div>
                </div>
                <ChevronDown className={`h-5 w-5 text-purple-400 transition-transform ${showNetworkDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showNetworkDropdown && (
                <div className="absolute z-10 w-full mt-2 bg-gray-800 border border-purple-500/30 rounded-xl shadow-xl overflow-hidden">
                  {availableNetworks.map((network) => (
                    <button
                      key={network.id}
                      type="button"
                      onClick={() => {
                        setSelectedNetwork(network.id);
                        setShowNetworkDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left hover:bg-purple-900/30 transition-colors ${
                        selectedNetwork === network.id ? 'bg-purple-900/40' : ''
                      }`}
                    >
                      <div className="font-medium text-sm">{network.name}</div>
                      <div className="text-xs text-gray-400">
                        Min: {network.minWithdrawal} {selectedCrypto} • Fee: {network.fee} {selectedCrypto}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Amount Input - Purple Accent */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-gray-300">Amount ({selectedCrypto})</label>
            <button
              type="button"
              onClick={handleMaxAmount}
              className="text-xs text-purple-400 hover:text-purple-300 font-medium px-2 py-1 bg-purple-500/20 rounded-lg"
            >
              MAX
            </button>
          </div>
          <div className="relative">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`Enter amount in ${selectedCrypto}`}
              step="any"
              min="0"
              className="w-full px-4 py-3 bg-gray-900/50 text-white rounded-xl border border-purple-500/30 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all pr-20"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-purple-400 font-medium">
              {selectedCrypto}
            </span>
          </div>
          <div className="flex justify-between mt-1">
            <p className="text-xs text-gray-500">
              Min: {currentNetwork?.minWithdrawal} {selectedCrypto}
            </p>
            {withdrawalAmount > 0 && (
              <p className="text-xs text-purple-400">
                ≈ ${fmtFixed(withdrawalUsdValue, 2)} USD
              </p>
            )}
          </div>
        </div>

        {/* Wallet Address - Purple Accent */}
        <div>
          <label className="text-sm font-semibold text-gray-300 mb-2 block">Withdrawal Address</label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder={`Enter your ${selectedCrypto} wallet address`}
            className="w-full px-4 py-3 bg-gray-900/50 text-white rounded-xl border border-purple-500/30 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all font-mono text-sm"
          />
        </div>

        {/* Enhanced Summary */}
        {withdrawalAmount > 0 && (
          <div className="bg-gradient-to-br from-gray-900/80 to-purple-900/20 rounded-xl p-4 space-y-3 border border-purple-500/20">
            <h4 className="text-sm font-semibold text-purple-300 flex items-center">
              <TrendingUp className="h-4 w-4 mr-2" />
              Withdrawal Summary
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Withdrawal Amount</span>
                <span className="text-white font-medium">{formatCryptoAmount(withdrawalAmount, selectedCrypto)} {selectedCrypto}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">USD Value</span>
                <span className="text-purple-300">${fmtFixed(withdrawalUsdValue, 2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Network Fee</span>
                <span className="text-orange-400">-{networkFee} {selectedCrypto}</span>
              </div>
              <div className="border-t border-purple-500/20 pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="text-gray-300 font-medium">Total Deducted</span>
                  <span className="text-white font-bold">{formatCryptoAmount(totalAmount, selectedCrypto)} {selectedCrypto}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-400">You Will Receive</span>
                  <span className="text-green-400 font-semibold">{formatCryptoAmount(receiveAmount, selectedCrypto)} {selectedCrypto}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-400">Balance After</span>
                  <span className="text-purple-300">{formatCryptoAmount(Math.max(0, cryptoBalance - totalAmount), selectedCrypto)} {selectedCrypto}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Warning */}
        <div className="flex items-start space-x-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
          <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-yellow-400 font-medium">Important</p>
            <p className="text-gray-400 text-xs mt-1">
              Ensure the address and network are correct. Withdrawals to wrong addresses cannot be recovered.
            </p>
          </div>
        </div>

        {/* Submit Button - Purple Gradient */}
        <button
          type="submit"
          disabled={isSubmitting || loadingHoldings || withdrawalAmount <= 0 || totalAmount > cryptoBalance || !address}
          className="w-full py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded-xl font-bold transition-all flex items-center justify-center space-x-2 shadow-lg shadow-purple-500/20"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Processing Withdrawal...</span>
            </>
          ) : (
            <>
              <ArrowUpRight className="h-5 w-5" />
              <span>Submit Withdrawal Request</span>
            </>
          )}
        </button>

        {/* Success Message */}
        {withdrawalSuccess && (
          <div className="flex items-center justify-center space-x-2 p-3 bg-green-500/20 border border-green-500/30 rounded-xl">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <span className="text-green-400 font-medium">Withdrawal submitted successfully!</span>
          </div>
        )}
      </form>
    </div>
  );
};

export default WithdrawalTab;
